import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Organisation, OrganisationControllerService, SimplePlace, User} from "../../../../api";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidationErrors,
  Validators
} from "@angular/forms";
import {Observable, Subject, Subscription, takeUntil} from "rxjs";
import {PermissionService} from "../../../../services/permission/permission.service";
import {faUserPlus} from "@fortawesome/pro-solid-svg-icons";
import {ProjectStateReversePipe} from "../../../../pipes/project-state.pipe";
import { KeyValue, KeyValuePipe, NgTemplateOutlet } from "@angular/common";
import {CanDeactivateComponent} from "../../../../guards/unsaved-changes.guard";
import {ActivatedRoute} from '@angular/router';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {MatTableDataSource} from "@angular/material/table";
import {StateConfigService} from "../../../../services/state/state-config.service";
import {
  austria,
  belgium,
  bulgaria,
  checkVAT,
  croatia,
  cyprus,
  czechRepublic,
  denmark,
  estonia,
  finland,
  france,
  germany,
  greece,
  hungary,
  ireland,
  italy,
  latvia,
  lithuania,
  luxembourg,
  malta,
  netherlands,
  poland,
  portugal,
  romania,
  slovakiaRepublic,
  slovenia,
  spain,
  sweden
} from 'jsvat';

import {UsersListComponent} from "../../../util/users/users.component";
import {LegalFormsPipe} from "../../../../pipes/legal-forms.pipe";
import {AddressInputComponent, applyDefaultCountry} from "../../../util/addressInput/address-input.component";
import {
  BicAccordionBodyComponent,
  BicAccordionComponent,
  BicAccordionElementComponent,
  BicAccordionHeaderComponent,
  BicButtonComponent,
  BicDropdownElementComponent,
  BicInputComponent,
  BicPopupComponent,
  BicSelectComponent,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";
import {PageHeaderComponent} from "../../../util/design/page-header/page-header.component";
import {OrganisationImageUploadComponent} from "../../../util/organisation-image-upload/organisation-image-upload.component";
import LegalFormEnum = Organisation.LegalFormEnum;
import StateEnum = Organisation.StateEnum;
import {NotificationsService} from '../../../../services/notifications/notifications.service';
import {OrganisationDataSourcesComponent} from "../organisation-data-sources/organisation-data-sources.component";
import {notBlankValidator} from '../../../../models/project-validators';


@Component({
  selector: 'app-organisationList-settings',
  templateUrl: './organisation-settings.component.html',
  styleUrls: ['./organisation-settings.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    UsersListComponent,
    KeyValuePipe,
    LegalFormsPipe,
    AddressInputComponent,
    BicSelectComponent,
    BicDropdownElementComponent,
    NgTemplateOutlet,
    BicInputComponent,
    BicButtonComponent,
    TooltipDirective,
    BicAccordionElementComponent,
    BicAccordionHeaderComponent,
    BicAccordionBodyComponent,
    BicAccordionComponent,
    PageHeaderComponent,
    OrganisationImageUploadComponent,
    OrganisationDataSourcesComponent
]
})
export class OrganisationSettingsComponent implements OnInit, OnDestroy, CanDeactivateComponent {
  permissionService = inject(PermissionService);
  dialog = inject(MatDialog);
  private formBuilder = inject(UntypedFormBuilder);
  private organisationControllerService = inject(OrganisationControllerService);
  private route = inject(ActivatedRoute);
  protected stateService = inject(StateConfigService);
  private notificationsService = inject(NotificationsService);


  clearEvent: Subject<void> = new Subject<void>();

  private eventsSubscription!: Subscription;
  @Input() clearEventTriggered!: Observable<void>;
  private organisationChangedSubscription!: Subscription;
  @Input() organisationChangedEventTriggered!: Observable<Organisation>;

  @Output() organisationSaved = new EventEmitter<Organisation>();
  organisations: Organisation[] = [];
  organisation!: Organisation | undefined;
  organisationId!: number;

  leaveOrDelete: boolean | undefined;

  generalInfoForm!: FormGroup;
  addressInfoForm!: FormGroup;
  locationGuesses$!: Observable<SimplePlace[]>;

  legalForm: typeof LegalFormEnum = LegalFormEnum;

  organisationChangedEvent: Subject<Organisation> = new Subject<Organisation>();

  /** Nach einem Microsoft-Callback (Query-Parameter der Datenquellen) den Bereich Datenquellen öffnen. */
  openedPanel = this.route.snapshot.queryParamMap.has('datasource') || this.route.snapshot.queryParamMap.has('datasourceError') ? 3 : 0;

  states: typeof StateEnum = StateEnum;
  dataSource!: MatTableDataSource<Organisation>;

  unsubscribe = new Subject<void>()
  private dialogAlreadyShown = false;


  @ViewChild('saveButton') saveButton!: HTMLButtonElement;

  user: User | undefined;

  countries = [
    //{ 'jsvat': 'andorra', 'code': 'AD', 'country': 'AND' },
    { 'jsvat': austria, 'code': 'AT', 'country': 'AUT' },
    { 'jsvat': belgium, 'code': 'BE', 'country': 'BEL' },
    //{ 'jsvat': 'brazil', 'code': 'BR', 'country': 'BRA' },
    { 'jsvat': bulgaria, 'code': 'BG', 'country': 'BGR' },
    //{ 'jsvat': 'switzerland', 'code': 'CH', 'country': 'CHE' },
    { 'jsvat': cyprus, 'code': 'CY', 'country': 'CYP' },
    { 'jsvat': czechRepublic, 'code': 'CZ', 'country': 'CZE' },
    { 'jsvat': germany, 'code': 'DE', 'country': 'DEU' },
    { 'jsvat': denmark, 'code': 'DK', 'country': 'DNK' },
    { 'jsvat': greece, 'code': 'GR', 'country': 'GRC' },
    { 'jsvat': spain, 'code': 'ES', 'country': 'ESP' },
    { 'jsvat': estonia, 'code': 'EE', 'country': 'EST' },
    { 'jsvat': finland, 'code': 'FI', 'country': 'FIN' },
    { 'jsvat': france, 'code': 'FR', 'country': 'FRA' },
    //{ 'jsvat': 'unitedKingdom', 'code': 'GB', 'country': 'GBR' },
    { 'jsvat': croatia, 'code': 'HR', 'country': 'HRV' },
    { 'jsvat': hungary, 'code': 'HU', 'country': 'HUN' },
    { 'jsvat': ireland, 'code': 'IE', 'country': 'IRL' },
    { 'jsvat': italy, 'code': 'IT', 'country': 'ITA' },
    { 'jsvat': latvia, 'code': 'LV', 'country': 'LVA' },
    { 'jsvat': lithuania, 'code': 'LT', 'country': 'LTU' },
    { 'jsvat': luxembourg, 'code': 'LU', 'country': 'LUX' },
    { 'jsvat': malta, 'code': 'MT', 'country': 'MLT' },
    { 'jsvat': netherlands, 'code': 'NL', 'country': 'NLD' },
    //{ 'jsvat': 'norway', 'code': 'NO', 'country': 'NOR' },
    { 'jsvat': poland, 'code': 'PL', 'country': 'POL' },
    { 'jsvat': portugal, 'code': 'PT', 'country': 'PRT' },
    { 'jsvat': romania, 'code': 'RO', 'country': 'ROU' },
    //{ 'jsvat': 'russiaFederation', 'code': 'RU', 'country': 'RUS' },
    //{ 'jsvat': 'serbia', 'code': 'RS', 'country': 'SRB' },
    { 'jsvat': slovenia, 'code': 'SI', 'country': 'SVN' },
    { 'jsvat': slovakiaRepublic, 'code': 'SK', 'country': 'SVK' },
    { 'jsvat': sweden, 'code': 'SE', 'country': 'SWE' }
  ]

  ngOnInit(): void {
    // Subscribe to state service updates - only once per component lifetime
    this.stateService.updated.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe((data) => {
      this.syncFormPermissions();
    });

    this.route.params.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(params => {
      const organisationId = params['id'];
      this.organisationId = params['id'];
      this.organisationControllerService.getOrganisation(organisationId).pipe(
        takeUntil(this.unsubscribe)
      ).subscribe((data) => {
        this.organisation = data;
        this.organisationChangedEvent.next(this.organisation);
        this.generalInfoForm = this.formBuilder.group({
          id: new FormControl(""),
          name: new UntypedFormControl("", [Validators.required, notBlankValidator]),
          legalForm: new UntypedFormControl(),
          organisationMail: new UntypedFormControl("", [Validators.required, Validators.email]),
          vatNumber: new UntypedFormControl("",[this.checkVATField])
        });

        this.addressInfoForm = this.formBuilder.group({
          street: new UntypedFormControl(""),
          houseNumber: new UntypedFormControl(""),
          zipCode: new UntypedFormControl("", [Validators.required, Validators.pattern('^[0-9]{4,5}$')]),
          city: new UntypedFormControl("", [Validators.required]),
          country: new UntypedFormControl("", [Validators.required]),
          state: new UntypedFormControl(""),
        });

        this.generalInfoForm.patchValue(this.organisation!)
        this.addressInfoForm.patchValue(this.organisation!)
        this.applyDefaultCountryIfEditable();

        // Check permissions and disable/enable forms if needed
        this.syncFormPermissions();

        // Subscribe to country changes to auto-update VAT number
        this.generalInfoForm.get('country')?.valueChanges.pipe(
          takeUntil(this.unsubscribe)
        ).subscribe((newValue: String) => {
          const vatControl = this.generalInfoForm.get('vatNumber');
          const existingVatValue = vatControl?.value;

          if (!existingVatValue || this.countries.some((country: { code: any; }) => country.code === existingVatValue)) {
            const countryCode = this.getCountry(newValue)?.code
            vatControl?.setValue(countryCode);
          }
        });

      });

    });


  }

  discardChanges() {
    // Reset forms to their saved state
    if (this.organisation) {
      this.generalInfoForm.patchValue(this.organisation);
      this.addressInfoForm.patchValue(this.organisation);
      this.applyDefaultCountryIfEditable();
      this.generalInfoForm.markAsPristine();
      this.addressInfoForm.markAsPristine();
      this.generalInfoForm.markAsUntouched();
      this.addressInfoForm.markAsUntouched();
    }
  }

  getCountry(countryName: String){
    // @ts-ignore
    for (const country of this.countries) {
      if (countryName === country.country) {
        return country;
      }
    }
    return null;
  }

  validForm(): boolean {

    return (this.generalInfoForm.valid || this.generalInfoForm.disabled) && (this.addressInfoForm.valid || this.addressInfoForm.disabled);
  }

  disableForm(): boolean {

    return this.generalInfoForm.disabled && this.addressInfoForm.disabled;
  }

  canDeactivate(): boolean {

    return this.generalInfoForm.pristine && this.addressInfoForm.pristine;
  }


  updateLocation(location: SimplePlace) {
    let projectStateReversePipe = new ProjectStateReversePipe()

    this.addressInfoForm.get("street")?.setValue(location.street);
    this.addressInfoForm.get("houseNumber")?.setValue(location.addressNumber);
    this.addressInfoForm.get("zipCode")?.setValue(location.postalCode);
    this.addressInfoForm.get("city")?.setValue(location.municipality);
    this.addressInfoForm.get("country")?.setValue(location.country);
    if (location.region != null && location.country == "DEU") {
      this.addressInfoForm.get("state")?.enable();
      this.addressInfoForm.get("state")?.setValue(projectStateReversePipe.transform(location.region));
    } else {
      this.addressInfoForm.get("state")?.setValue(undefined);
      this.addressInfoForm.get("state")?.disable();
    }
  }

  valueAscOrder = (a: KeyValue<string, string>, b: KeyValue<string, string>): number => {
    return a.value.localeCompare(b.value);
  }


  get saveDisabled() {
    return this.generalInfoForm.invalid || this.addressInfoForm.invalid;
  }

  save() {
    this.organisation = {
      ...this.generalInfoForm.value,
      ...this.addressInfoForm.value
    };
    if (this.organisation?.vatNumber && this.organisation.country && this.organisation.vatNumber==this.getCountry(this.organisation.country)?.code){
      this.organisation.vatNumber = undefined
    }

    this.organisationControllerService.updateOrganisation(this.organisation!, this.organisationId).subscribe(organisation => {
      this.generalInfoForm.markAsPristine();
      this.addressInfoForm.markAsPristine();
      this.organisation = organisation;
      this.stateService.organisationUpdated(organisation);
      this.organisationSaved.emit(this.organisation);
      this.notificationsService.showSuccessMessage("Änderungen gespeichert")
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.eventsSubscription?.unsubscribe();
    this.organisationChangedSubscription?.unsubscribe();
  }

  leaveDialog() {
    const dialogRef = this.dialog.open(OrgLeaveConfirmationDialog, {
      panelClass: 'rounded-corners-dialog',
      width: '650px',
      data: {
        organisation: this.organisation
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.leaveOrganisation(this.organisation!)
      }
    });
  }

  leaveOrganisation(org: Organisation) {
    this.clearEvent.next();
    if (org.id != undefined) {
      this.organisationControllerService.leaveOrganisation(org.id).subscribe(() => {
        this.organisations = this.organisations.filter(obj => obj !== org);
        this.dataSource = new MatTableDataSource(this.organisations);
        this.stateService.selectProject(undefined, false);
        this.stateService.selectOrganisation(undefined, true, false, true);
      });
    }
  }

  deleteDialog() {
    const dialogRef = this.dialog.open(OrgDeleteConfirmationDialog, {
      panelClass: 'rounded-corners-dialog',
      width: '650px',
      data: {
        organisation: this.organisation,
        leaveOrDelete: this.leaveOrDelete
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        if (result) {
          this.deleteOrganisation(this.organisation!)
        } else {
          this.leaveOrganisation(this.organisation!)
        }
      }
    });
  }

  /**
   * Deutschland vorbelegen, wenn das Land leer ist. Die Rechteprüfung ist nötig, weil
   * syncFormPermissions() erst per setTimeout deaktiviert - das Control ist hier noch enabled.
   */
  private applyDefaultCountryIfEditable(): void {
    if (this.organisation && this.permissionService.hasOrganisationPrivilege('ORGANISATION_SETTINGS', this.organisation.id!)) {
      applyDefaultCountry(this.addressInfoForm.get('country'));
    }
  }

  private syncFormPermissions(): void {
    if (!this.organisation || !this.generalInfoForm || !this.addressInfoForm) {
      return;
    }

    const canEdit = this.permissionService.hasOrganisationPrivilege('ORGANISATION_SETTINGS', this.organisation.id!);
    setTimeout(() => {
      if (canEdit) {
        this.generalInfoForm.enable();
        this.addressInfoForm.enable();
      } else {
        this.generalInfoForm.disable();
        this.addressInfoForm.disable();
      }
    }, 0);
  }



  deleteOrganisation(org: Organisation) {
    this.clearEvent.next();
    if (org.id != undefined) {
      this.organisationControllerService.deleteOrganisation(org.id).subscribe(() => {
        this.organisations = this.organisations.filter(obj => obj !== org);
        this.dataSource = new MatTableDataSource(this.organisations);
        this.stateService.selectProject(undefined, false);
        this.stateService.selectOrganisation(undefined, true, false, true);
      });
    }
  }

  private checkVATField = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    // Only validate if both forms are initialized
    if (!this.addressInfoForm || !this.generalInfoForm) {
      return null;
    }
    const country = this.getCountry(this.addressInfoForm.get("country")?.value)
    if(value != undefined && value != ""){
      if (country && country.code!=value && !checkVAT(value, [country.jsvat]).isValid) {
        return { validVATRequired: true };
      }
    }
    return null; // Validation passed
  }


  protected readonly faUserPlus = faUserPlus;
}

@Component({
  selector: 'organisationList-delete-confirmation-dialog',
  templateUrl: './organisation-delete-confirmation.component.html',
  styleUrls: ['./organisation-settings.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent

  ]
})
export class OrgDeleteConfirmationDialog implements OnInit {
  data = inject<{
    organisation: Organisation;
    leaveOrDelete: boolean;
  }>(MAT_DIALOG_DATA);
  permissionService = inject(PermissionService);
  private dialogRef = inject<MatDialogRef<OrgDeleteConfirmationDialog>>(MatDialogRef);


  existsOtherAdminMap: Map<Organisation, boolean> = new Map<Organisation, boolean>();

  ngOnInit() {
  }

  leave(): void {
    this.data.leaveOrDelete = false;
    this.dialogRef.close(this.data.leaveOrDelete);
  }

  delete(): void {
    this.data.leaveOrDelete = true;
    this.dialogRef.close(this.data.leaveOrDelete);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}

@Component({
  selector: 'organisationList-leave-confirmation-dialog',
  templateUrl: './organisation-leave-confirmation.component.html',
  styleUrls: ['./organisation-settings.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BicButtonComponent,
    BicPopupComponent
  ]
})
export class OrgLeaveConfirmationDialog implements OnInit {
  data = inject<{
    organisation: Organisation;
  }>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<OrgLeaveConfirmationDialog>>(MatDialogRef);


  ngOnInit() {
  }

  leave(): void {
    this.dialogRef.close(true);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
